"use client";

import { useEffect, useRef, useState } from "react";

export interface RevealOptions
{
    /** Reveal once and stop observing, instead of re-hiding on the way out. */
    once?: boolean;
    /**
     * Shrinks the box the observer tests against. A negative bottom holds the
     * reveal back until the element is properly inside the viewport rather than
     * firing on its first pixel.
     */
    rootMargin?: string;
}

// Drop-in replacement for the framer-motion scroll-reveal pattern (useAnimation + useInView +
// m.div + variants), but with ZERO animation-library JS — just an IntersectionObserver that
// toggles a class. The actual fade/slide is pure CSS (reveal.module.scss, fade-in.module.scss),
// so removing framer-motion (~35KB) doesn't change the visual reveal and works in every browser.
// Re-hides when it leaves the viewport (matching the old `controls.start(inView ? "visible" :
// "hidden")`); pass once=true — or {once: true} — to reveal a single time.
export function useReveal<T extends HTMLElement = HTMLDivElement>(options: boolean | RevealOptions = false) {
  const { once = false, rootMargin } = typeof options === "boolean" ? {once: options, rootMargin: undefined} : options;
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible) {
        setShown(true);
        if (once) io.disconnect();
      } else if (!once) {
        setShown(false);
      }
    }, rootMargin === undefined ? undefined : {rootMargin});
    io.observe(el);
    return () => io.disconnect();
  }, [once, rootMargin]);

  return { ref, shown };
}
